// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Where the modify commands' batches come from (frontend-u0o.4): the one
 * place that decides, per command, whether the client computes the
 * `*.update` / `*.create` ops itself or sends the server's own op.
 *
 * Since ruxd-qfc (ruxd #30) the server has `wall.extend_to`,
 * `selection.transform` and `wall.offset`: Forlæng / trim, Flyt / Kopiér /
 * Rotér and Forskyd send those (`SERVER_OPS`), so the server re-solves the
 * joins (a tee ends on the target's face, a corner moves both ends) and
 * says what it dropped in the result's `warnings`. Each is first computed
 * here too — that refuses a bad pick in Danish before any request, and
 * `applyOps` draws the same client ops as the preview and the optimistic
 * overlay (`expandServerOp`). Spejl stays client-side by design; Juster is
 * a move along the wall's normal (`wall.update`s).
 *
 * Pure: no React, no DOM.
 */

import type { ModelEditOp } from '../api/types';
import type { GeometriModel } from './geometri';
import type { XY } from './planWalls';
import {
  alignOps,
  extendEnd,
  extendEndOps,
  offsetCopyOps,
  reflection,
  rotation,
  transformOps,
  translation,
  type AlignReference,
  type OpsResult,
} from './wallModify';

/** Which commands the server computes (ruxd-qfc). Flip one back to compute it here. */
export const SERVER_OPS = {
  extendTo: true,
  transform: true,
  offset: true,
} as const;

const r6 = (v: number) => Math.round(v * 1e6) / 1e6 + 0;

/** Forlæng / trim (TR): wall `guid` to boundary `boundary`, keeping the part `keep` lies on. */
export function extendTo(model: GeometriModel, boundary: string, guid: string, keep: XY): OpsResult {
  const e = extendEnd(model, boundary, guid, keep);
  if ('error' in e) return e;
  const local = extendEndOps(model, boundary, guid, e.end);
  if ('error' in local || !SERVER_OPS.extendTo) return local;
  return { ops: [{ op: 'wall.extend_to', guid, end: e.end, target: boundary }] };
}

/** Forskyd (OF): a copy of wall `guid` `offset` metres along its left normal. */
export function offsetWall(model: GeometriModel, guid: string, offset: number, newGuid: string): OpsResult {
  const local = offsetCopyOps(model, guid, offset, newGuid);
  if ('error' in local || !SERVER_OPS.offset) return local;
  return { ops: [{ op: 'wall.offset', guid, distance: r6(offset), copy: true, new_guid: newGuid }] };
}

/**
 * A selection transform: computed here (to check it, and for its copies'
 * guids), sent as `selection.transform` — rotation `angle` (radians, ccw)
 * about `pivot`, then `delta`.
 */
function transform(model: GeometriModel, guids: readonly string[], delta: XY, angle: number, pivot: XY, copy: boolean, newGuid: () => string): OpsResult {
  const names: Record<string, string> = {};
  const r = rotation(pivot, angle);
  const T = angle === 0 ? translation(delta) : { m: r.m, t: [r.t[0] + delta[0], r.t[1] + delta[1]] as XY };
  const local = transformOps(model, guids, T, { copy, newGuid: (g) => (names[g] ??= newGuid()) });
  if ('error' in local || local.ops.length === 0 || !SERVER_OPS.transform) return local;
  const known = new Set([...(model.walls ?? []), ...(model.openings ?? []), ...(model.spaces ?? [])].map((x) => x.guid));
  const op: ModelEditOp = {
    op: 'selection.transform',
    guids: guids.filter((g) => known.has(g)),
    ...(delta[0] !== 0 || delta[1] !== 0 ? { translate: [r6(delta[0]), r6(delta[1])] as [number, number] } : {}),
    ...(angle !== 0 ? { rotate_deg: r6((angle * 180) / Math.PI), pivot: [r6(pivot[0]), r6(pivot[1])] as [number, number] } : {}),
    ...(copy ? { copy: true, new_guids: names } : {}),
  };
  return { ops: [op] };
}

/** Flyt / Kopiér (MV / CO): the selection by `delta`. */
export function moveSelection(model: GeometriModel, guids: readonly string[], delta: XY, copy: boolean, newGuid: () => string): OpsResult {
  return transform(model, guids, delta, 0, [0, 0], copy, newGuid);
}

/** Rotér (RO): the selection by `angle` radians (counter-clockwise) about `pivot`. */
export function rotateSelection(model: GeometriModel, guids: readonly string[], pivot: XY, angle: number, copy: boolean, newGuid: () => string): OpsResult {
  return transform(model, guids, [0, 0], angle, pivot, copy, newGuid);
}

/** Spejl (MM): the selection mirrored in the line `a`–`b` — client-side by design. */
export function mirrorSelection(model: GeometriModel, guids: readonly string[], a: XY, b: XY, copy: boolean, newGuid: () => string): OpsResult {
  const T = reflection(a, b);
  if (!T) return { error: 'Spejlaksen skal have en længde' };
  return transformOps(model, guids, T, { copy, newGuid: () => newGuid() });
}

/** Juster (AL): wall `guid` onto the reference line. */
export function alignWall(model: GeometriModel, ref: AlignReference, guid: string): OpsResult {
  return alignOps(model, ref, guid);
}
